import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Send, ChevronsUpDown, Check } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

const DEPARTEMENTS = [
  "01 Ain", "02 Aisne", "03 Allier", "04 Alpes-de-Haute-Provence", "05 Hautes-Alpes",
  "06 Alpes-Maritimes", "07 Ardèche", "08 Ardennes", "09 Ariège", "10 Aube",
  "11 Aude", "12 Aveyron", "13 Bouches-du-Rhône", "14 Calvados", "15 Cantal",
  "16 Charente", "17 Charente-Maritime", "18 Cher", "19 Corrèze", "21 Côte-d'Or",
  "22 Côtes-d'Armor", "23 Creuse", "24 Dordogne", "25 Doubs", "26 Drôme",
  "27 Eure", "28 Eure-et-Loir", "29 Finistère", "30 Gard", "31 Haute-Garonne",
  "32 Gers", "33 Gironde", "34 Hérault", "35 Ille-et-Vilaine", "36 Indre",
  "37 Indre-et-Loire", "38 Isère", "39 Jura", "40 Landes", "41 Loir-et-Cher",
  "42 Loire", "43 Haute-Loire", "44 Loire-Atlantique", "45 Loiret", "46 Lot",
  "47 Lot-et-Garonne", "48 Lozère", "49 Maine-et-Loire", "50 Manche", "51 Marne",
  "52 Haute-Marne", "53 Mayenne", "54 Meurthe-et-Moselle", "55 Meuse", "56 Morbihan",
  "57 Moselle", "58 Nièvre", "59 Nord", "60 Oise", "61 Orne",
  "62 Pas-de-Calais", "63 Puy-de-Dôme", "64 Pyrénées-Atlantiques", "65 Hautes-Pyrénées", "66 Pyrénées-Orientales",
  "67 Bas-Rhin", "68 Haut-Rhin", "69 Rhône", "70 Haute-Saône", "71 Saône-et-Loire",
  "72 Sarthe", "73 Savoie", "74 Haute-Savoie", "75 Paris", "76 Seine-Maritime",
  "77 Seine-et-Marne", "78 Yvelines", "79 Deux-Sèvres", "80 Somme", "81 Tarn",
  "82 Tarn-et-Garonne", "83 Var", "84 Vaucluse", "85 Vendée", "86 Vienne",
  "87 Haute-Vienne", "88 Vosges", "89 Yonne", "90 Territoire de Belfort", "91 Essonne",
  "92 Hauts-de-Seine", "93 Seine-Saint-Denis", "94 Val-de-Marne", "95 Val-d'Oise"
];

const ContactForm = () => {
  // Fonction pour obtenir ou définir la source originale
  const getOriginalSource = () => {
    if (typeof window === 'undefined') return '';
    
    // Vérifier si on a déjà stocké la source originale
    let originalSource = sessionStorage.getItem('originalSource');
    
    if (!originalSource) {
      // Si c'est la première visite, stocker le referrer actuel
      const referrer = document.referrer;
      
      // Parser le domaine du referrer pour avoir une source plus claire
      if (referrer) {
        try {
          const url = new URL(referrer);
          const hostname = url.hostname;
          
          // Identifier les sources communes
          if (hostname.includes('google')) {
            originalSource = 'Google';
          } else if (hostname.includes('linkedin')) {
            originalSource = 'LinkedIn';
          } else if (hostname.includes('facebook')) {
            originalSource = 'Facebook';
          } else if (hostname.includes('instagram')) {
            originalSource = 'Instagram';
          } else if (hostname.includes('twitter') || hostname.includes('x.com')) {
            originalSource = 'Twitter/X';
          } else if (hostname.includes('bing')) {
            originalSource = 'Bing';
          } else if (hostname.includes('chat.openai') || hostname.includes('chatgpt')) {
            originalSource = 'ChatGPT';
          } else if (hostname.includes('claude')) {
            originalSource = 'Claude';
          } else if (hostname.includes('perplexity')) {
            originalSource = 'Perplexity';
          } else if (hostname.includes('youtube')) {
            originalSource = 'YouTube';
          } else if (hostname.includes('tiktok')) {
            originalSource = 'TikTok';
          } else if (hostname.includes('workandyou')) {
            originalSource = 'WorkAndYou.fr';
          } else {
            // Pour les autres sources, garder le nom de domaine complet
            originalSource = `${hostname} (${referrer})`;
          }
        } catch (e) {
          originalSource = referrer;
        }
      } else {
        originalSource = 'Direct (aucun referrer)';
      }
      
      sessionStorage.setItem('originalSource', originalSource);
    }
    
    return originalSource;
  };

  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    prenom: "",
    nom: "",
    email: "",
    telephone: "",
    departement: "",
    referrer: ""
  });
  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const { toast } = useToast();

  // Définir la source originale au chargement du composant
  useEffect(() => {
    const source = getOriginalSource();
    setFormData(prev => ({ ...prev, referrer: source }));
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const { error } = await supabase.functions.invoke('send-contact-email', {
        body: formData
      });

      if (error) {
        throw error;
      }

      // Redirect to thank you page
      navigate('/thank-you');
      
    } catch (error) {
      console.error('Error sending email:', error);
      toast({
        title: "Erreur",
        description: "Une erreur est survenue. Veuillez réessayer.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section id="contact" className="py-20 gradient-bg text-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-20 w-72 h-72 bg-white/5 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-white/5 rounded-full blur-3xl animate-float" style={{animationDelay: '2s'}}></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            📩 <span className="text-white">Contactez-nous</span>
          </h2>
          <p className="text-xl opacity-90 max-w-3xl mx-auto leading-relaxed">
            Vous êtes prêt(e) à démarrer une nouvelle aventure professionnelle, en étant bien entouré(e) ? 
            Remplissez le formulaire ci-dessous.
          </p>
          <p className="text-lg mt-4 text-white/90 font-medium">
            Nous vous recontactons sous 48h.
          </p>
        </div>

        <Card className="max-w-2xl mx-auto bg-white/10 backdrop-blur-md border-white/20">
          <CardContent className="p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2 text-white">Prénom *</label>
                  <Input
                    type="text"
                    name="prenom"
                    value={formData.prenom}
                    onChange={handleChange}
                    required
                    className="bg-white/20 border-white/30 text-white placeholder:text-white/60 focus:border-white focus:ring-white/50"
                    placeholder="Votre prénom"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-white">Nom *</label>
                  <Input
                    type="text"
                    name="nom"
                    value={formData.nom}
                    onChange={handleChange}
                    required
                    className="bg-white/20 border-white/30 text-white placeholder:text-white/60 focus:border-white focus:ring-white/50"
                    placeholder="Votre nom"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2 text-white">Email *</label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="bg-white/20 border-white/30 text-white placeholder:text-white/60 focus:border-white focus:ring-white/50"
                  placeholder="votre.email@exemple.com"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2 text-white">Téléphone *</label>
                <Input
                  type="tel"
                  name="telephone"
                  value={formData.telephone}
                  onChange={handleChange}
                  required
                  className="bg-white/20 border-white/30 text-white placeholder:text-white/60 focus:border-white focus:ring-white/50"
                  placeholder="06 12 34 56 78"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-white">Département *</label>
                <Popover open={open} onOpenChange={setOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={open}
                      aria-label="Sélectionner un département"
                      className="w-full justify-between bg-white/20 border-white/30 text-white hover:bg-white/30 hover:text-white"
                    >
                      {formData.departement || "Sélectionnez un département..."}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Rechercher un département..." className="h-9" />
                      <CommandList>
                        <CommandEmpty>Aucun département trouvé.</CommandEmpty>
                        <CommandGroup>
                          {DEPARTEMENTS.map((dept) => (
                            <CommandItem
                              key={dept}
                              value={dept}
                              onSelect={(currentValue) => {
                                setFormData({ ...formData, departement: currentValue === formData.departement.toLowerCase() ? "" : dept });
                                setOpen(false);
                              }}
                            >
                              {dept}
                              <Check
                                className={cn(
                                  "ml-auto h-4 w-4",
                                  formData.departement === dept ? "opacity-100" : "opacity-0"
                                )}
                              />
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              <Button
                type="submit" 
                size="lg"
                disabled={isLoading}
                className="w-full bg-white text-primary hover:bg-white/90 text-lg py-6 rounded-xl shadow-2xl transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                <Send className="w-5 h-5 mr-2" />
                {isLoading ? "Envoi en cours..." : "Envoyer ma demande"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default ContactForm;
